import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Upload, FileText, Trash2, Loader2, Download } from "lucide-react";

const label = (c) => c.replace(/_/g, " ").replace(/^./, (s) => s.toUpperCase());
const kb = (b) => `${Math.max(1, Math.round((b || 0) / 1024))} KB`;

export const DocumentUploader = ({ caseId, closed = false }) => {
  const [state, setState] = useState(null);
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/verification/cases/${caseId}/documents`);
    setState(data);
    if ((data.required_categories || []).length) {
      setCategory((current) => current || data.required_categories[0]);
    }
  }, [caseId]);
  useEffect(() => { load().catch(() => setMsg("Documents could not be loaded. Please refresh to try again.")); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      await api.post(`/verification/cases/${caseId}/documents`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      setMsg("Document uploaded to secure storage.");
    } catch (err) { setMsg(err?.response?.data?.detail || "Upload failed."); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const view = async (f) => {
    const res = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank", "noopener");
  };

  const remove = async (f) => {
    try {
      await api.delete(`/files/${f.id}`);
      await load();
      setMsg("Document withdrawn.");
    } catch (err) { setMsg(err?.response?.data?.detail || "Could not withdraw this document."); }
  };

  if (!state) return msg ? <p role="alert" className="mt-4 text-red-700">{msg}</p> : null;
  return (
    <section className="card p-6 mt-6" data-testid="document-upload-panel">
      <h2 className="font-semibold">Upload your documents</h2>
      <p className="mt-2 text-xs text-slate-600">
        PDF, Word, JPG or PNG up to 25 MB. Files are stored in tenant-isolated object storage and are
        served through authenticated EuropEdu endpoints. Files must pass the configured security checks
        before they can be used; each document shows its scan status.
      </p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="block text-sm">
          <span className="text-slate-600">Document category</span>
          <select data-testid="document-category-select" className="field mt-1" value={category}
            onChange={(e) => setCategory(e.target.value)}>
            {(state.required_categories || []).map((c) => <option key={c} value={c}>{label(c)}</option>)}
          </select>
        </label>
        <button data-testid="upload-document-button" onClick={() => fileRef.current?.click()}
          disabled={busy || !category || closed} className="btn btn-primary">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
          Upload document
        </button>
        <input ref={fileRef} data-testid="document-file-input" type="file" className="hidden"
          accept=".pdf,.docx,.jpg,.jpeg,.png" onChange={upload} />
      </div>

      <ul className="mt-5 space-y-2" data-testid="document-list">
        {(state.documents || []).map((f) => (
          <li key={f.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-4 py-3"
            data-testid={`document-row-${f.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-900" />{f.original_filename}
              </p>
              <p className="text-xs text-slate-500">
                {label(f.category)} · {kb(f.size)} · SHA-256 {f.sha256?.slice(0, 12)}… · {f.malware_scan_status.replace(/_/g, " ")}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`view-document-${f.id}`} onClick={() => view(f)} className="btn btn-ghost py-1 px-3"><Download className="w-4 h-4" /></button>
              <button disabled={closed} data-testid={`remove-document-${f.id}`} onClick={() => remove(f)} className="btn btn-ghost py-1 px-3 text-rose-700"><Trash2 className="w-4 h-4" /></button>
            </div>
          </li>
        ))}
        {(state.documents || []).length === 0 && (
          <li className="text-sm text-slate-500" data-testid="no-documents">No documents uploaded yet.</li>
        )}
      </ul>
      {msg && <p className="mt-3 text-sm text-slate-800" data-testid="document-message">{msg}</p>}
    </section>
  );
};

